// Copyright (C) 2021 Intel Corporation
//
// SPDX-License-Identifier: MIT

// What one AI model saw on the card from one imaging source: its subgrades and its findings
// for each side, what the answer cost and who asked for it. Everything shown is already in the
// store; opening it costs nothing.

import React from 'react';
import Modal from 'antd/lib/modal';
import {
    ClockCircleOutlined, DatabaseOutlined, DollarOutlined, UserOutlined,
} from '@ant-design/icons';

import { AiGradeResult, AiSideGrades } from 'reducers/interfaces';
import { getGradeNickname } from 'utils/grades';
import {
    AI_GRADE_PROVIDER_LABELS, AI_GRADE_SOURCE_LABELS, formatCost, formatTokens, shortModelName,
} from 'utils/ai-grades';
import AiSparklesIcon from './ai-sparkles-icon';

type Side = 'front' | 'back';

const SUBGRADES: [keyof Pick<AiSideGrades, 'centering' | 'corners' | 'edges' | 'surface'>, string][] = [
    ['centering', 'Centering'],
    ['corners', 'Corners'],
    ['edges', 'Edges'],
    ['surface', 'Surface'],
];

function Grade({ value, size }: { value: number; size: 'large' | 'medium' }): JSX.Element {
    return (
        <span className={`ai-findings-grade ai-findings-grade-${size}`}>
            <strong>{value.toFixed(2)}</strong>
            <span className='ai-findings-nickname'>{getGradeNickname(value)}</span>
        </span>
    );
}

function SideFindings({ side, grades, total }: {
    side: Side;
    grades: AiSideGrades;
    total: number | null;
}): JSX.Element {
    return (
        <section className='ai-findings-side'>
            <header className='ai-findings-side-header'>
                <span className='ai-findings-side-name'>{side === 'front' ? 'Front' : 'Back'}</span>
                {total !== null ? <Grade value={total} size='medium' /> : null}
            </header>

            <ul className='ai-findings-bars'>
                {SUBGRADES.map(([key, title]) => (
                    <li key={key}>
                        <span className='ai-findings-bar-label'>{title}</span>
                        <span className='ai-findings-bar-track'>
                            <span className='ai-findings-bar-fill' style={{ width: `${grades[key] * 10}%` }} />
                        </span>
                        <span className='ai-findings-bar-value'>{grades[key].toFixed(1)}</span>
                    </li>
                ))}
            </ul>

            <div className='ai-findings-centering'>
                <span>
                    Left / right
                    <strong>{grades.centeringRatio.leftRight || '?'}</strong>
                </span>
                <span>
                    Top / bottom
                    <strong>{grades.centeringRatio.topBottom || '?'}</strong>
                </span>
            </div>

            <blockquote className='ai-findings-text'>{grades.notes || 'No findings reported.'}</blockquote>
        </section>
    );
}

interface Props {
    result: AiGradeResult | null;
    onClose(): void;
}

function AiFindingsModal({ result, onClose }: Props): JSX.Element | null {
    const grades = result?.grades ?? null;
    const label = result ? AI_GRADE_SOURCE_LABELS[result.source] : null;
    const capped = !!(result?.overall && result.overallUncapped && result.overall < result.overallUncapped);
    const gradedAt = result?.gradedAt ? new Date(result.gradedAt).toLocaleString() : null;
    const providerDot = `grades-table-provider-dot grades-table-provider-${result?.provider}`;

    return (
        <Modal
            visible={!!(result && grades)}
            onCancel={onClose}
            footer={null}
            width={920}
            destroyOnClose
            className='ai-findings-modal'
            title={null}
        >
            {result && label && grades ? (
                <>
                    <header className='ai-findings-hero'>
                        <span className='ai-findings-hero-icon'>
                            <AiSparklesIcon />
                        </span>
                        <div className='ai-findings-hero-text'>
                            <span className='ai-findings-eyebrow'>{`AI findings · ${label.title}`}</span>
                            <h3>{shortModelName(result.model)}</h3>
                            <span className='ai-findings-source'>
                                <span className={providerDot} />
                                {AI_GRADE_PROVIDER_LABELS[result.provider]}
                                <span className='ai-findings-dot' />
                                {result.answeredBy ?? result.model}
                                {result.effort ? (
                                    <>
                                        <span className='ai-findings-dot' />
                                        {`${result.effort} effort`}
                                    </>
                                ) : null}
                            </span>
                        </div>
                        {result.overall !== null ? (
                            <div className='ai-findings-overall'>
                                <span className='ai-findings-eyebrow'>Overall</span>
                                <Grade value={result.overall} size='large' />
                                {capped ? (
                                    <span className='ai-findings-capped'>
                                        {`Capped from ${(result.overallUncapped as number).toFixed(2)}: `}
                                        lowest combined subgrade + 1
                                    </span>
                                ) : null}
                            </div>
                        ) : null}
                    </header>

                    {result.status === 'error' && result.error ? (
                        <div className='ai-findings-error'>
                            The last run of this model failed, these findings are from the one before:
                            {` ${result.error}`}
                        </div>
                    ) : null}

                    <div className='ai-findings-sides'>
                        <SideFindings side='front' grades={grades.front} total={result.frontTotal} />
                        <SideFindings side='back' grades={grades.back} total={result.backTotal} />
                    </div>

                    <footer className='ai-findings-footer'>
                        {result.costUsd !== null ? (
                            <span>
                                <DollarOutlined />
                                {formatCost(result.costUsd)}
                            </span>
                        ) : null}
                        {result.usage ? (
                            <span>
                                <DatabaseOutlined />
                                {`${formatTokens(result.usage.inputTokens)} input`}
                                {result.usage.cachedInputTokens ? ` (${formatTokens(result.usage.cachedInputTokens)} cached)` : ''}
                                {` · ${formatTokens(result.usage.outputTokens)} output tokens`}
                            </span>
                        ) : null}
                        {result.elapsedMs !== null ? (
                            <span>
                                <ClockCircleOutlined />
                                {`${Math.round(result.elapsedMs / 1000)} s`}
                            </span>
                        ) : null}
                        <span className='ai-findings-footer-note'>
                            <UserOutlined />
                            {[
                                result.gradedBy ? `Run by ${result.gradedBy}` : null,
                                gradedAt,
                                result.promptVersion !== null ? `prompt v${result.promptVersion}` : null,
                            ].filter(Boolean).join(' · ')}
                        </span>
                    </footer>
                </>
            ) : null}
        </Modal>
    );
}

export default React.memo(AiFindingsModal);
